import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';

import BackOfficeDeviceEditDrawer from 'components/drawers/BackOfficeDeviceEditDrawer';
import BackOfficeFilterDrawer from 'components/drawers/BackOfficeFilterDrawer';
import BackOfficeSection from 'components/features/backOffice/BackOfficeSection';
import BackOfficeDeviceFilterForm from 'components/forms/BackOfficeDeviceFilterForm';
import BackOfficeDevicesRequest from 'components/requests/BackOfficeDevicesRequest';
import BackOfficeDevicesTable from 'components/tables/BackOfficeDevicesTable';
import { selectBackOfficeDeviceDataSources } from 'store/modules/backOfficeDevices/selectors';
import { FilterSetName } from 'store/modules/routerUtils/types';
import { useApplyCachedSearchParams } from 'utils/Router/use-apply-cached-search-params';

function BackOfficeDevices(): JSX.Element {
  const { pathname } = useLocation();
  const { applied } = useApplyCachedSearchParams(pathname);
  const [searchParams, setSearchParams] = useSearchParams();
  const dataSources = useSelector(selectBackOfficeDeviceDataSources);
  // This logic assumes there are two dataSource objects with
  // environments "Production" & "Staging"
  const environment = import.meta.env.VITE_ENV === 'production' ? 'Production' : 'Staging';
  const dataSourceForEnvironment = useMemo(() => dataSources && Object.values(dataSources).find(
    (dataSource) => dataSource.environment === environment
  ), [dataSources, environment]);

  const [isSearchParamsReady, setIsSearchParamsReady] = useState(false);
  useEffect(() => {
    if (!applied || isSearchParamsReady) {
      return;
    }

    // URL already has params (e.g. from cache), so we do not override.
    if (searchParams.size > 0) {
      setIsSearchParamsReady(true);
      return;
    }

    // Cached check has run and searchParams is still empty(*), so we apply defaultSearchParams now.
    // (*) Note that we accept one edge case: if user did clear filters before navigating away, cached check would run
    // and searchParams would still be empty; that "cleared state" will be overridden by defaultSearchParams when user
    // navigates back to the page.
    setSearchParams((prev) => {
      if (dataSourceForEnvironment) {
        const newSearchParams = new URLSearchParams(prev);
        newSearchParams.set(`filter.${FilterSetName.BACK_OFFICE_DEVICES}.dataSourceId`, dataSourceForEnvironment.id);
        return newSearchParams;
      }
      return prev;
    });
    setIsSearchParamsReady(true);
  }, [applied, dataSourceForEnvironment, isSearchParamsReady, searchParams, setSearchParams]);

  return (
    <BackOfficeSection
      table={<BackOfficeDevicesTable />}
      filterSetName={FilterSetName.BACK_OFFICE_DEVICES}
      searchInputPlaceholder="Search by Name/Serial #"
    >
      <BackOfficeDevicesRequest />
      <BackOfficeDeviceEditDrawer />
      <BackOfficeFilterDrawer filterSetName={FilterSetName.BACK_OFFICE_DEVICES}>
        <BackOfficeDeviceFilterForm />
      </BackOfficeFilterDrawer>
    </BackOfficeSection>
  );
}

export default BackOfficeDevices;
